import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import {
    AccessBookingType,
    GetBookingAccessCredentialsOutputDto,
} from '../../../models/access-control/dto/booking-access-credential.dto';

/**
 * Prefixed with `['game', gameId]` on purpose: the mutations that make the
 * server revoke + reissue codes (time change, add/kick participant) already
 * invalidate `GAME_QUERY_KEY(gameId)`, and react-query's prefix matching makes
 * that refresh the codes too. `organizationId` is last so it still scopes the
 * cache entry without breaking that prefix.
 */
export const GAME_ACCESS_CREDENTIALS_QUERY_KEY = (organizationId: string, gameId: number) =>
    ['game', gameId, 'access-credentials', organizationId];

/**
 * Access-control credentials (igloohome PINs and friends) issued for one game.
 * Court bookings materialize as games and are issued under
 * `AccessBookingType.GAME` too, so this covers both.
 *
 * The endpoint is org ADMIN-gated; a coach account gets a 403, which surfaces
 * as the card's error state rather than a broken page.
 */
export const useGetGameAccessCredentials = (
    organizationId: string | undefined,
    gameId: number,
) => {
    const apiClient = useApiClient();

    return useQuery<GetBookingAccessCredentialsOutputDto>({
        queryKey: GAME_ACCESS_CREDENTIALS_QUERY_KEY(organizationId || '', gameId),
        enabled: !!organizationId && !!gameId,
        queryFn: () =>
            apiClient.fetchApi<GetBookingAccessCredentialsOutputDto, {}>({
                method: 'GET',
                endpoint: `access-control/admin/credentials/${organizationId}/${AccessBookingType.GAME}/${gameId}`,
            }),
        // PINs change only on a reissue (time/court edit), so a short cache is
        // plenty and keeps the operator from re-fetching on every tab focus.
        staleTime: 60 * 1000,
    });
};
